//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _toUpper from 'lodash/toUpper';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { STRIPE_API_PUBLISHABLE_KEY } from '../../system/constants';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { requestEmpInviteCode, validateInviteCode } from '../../common/services/empMySettingsServices';
import {
    CONFIRM_DIALOG_REQUEST_INVITE_CODE_HEADING,
    CONFIRM_DIALOG_REQUEST_INVITE_CODE_MESSAGE,
    MESSAGE_REQUEST_INVITE_CODE_SUBMITTED_SUCCESSFULLY,
    MESSAGE_TITLE_REQUEST_INVITE_CODE_SUBMITTED_SUCCESSFULLY,
} from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import Subscription from './Subscription';
// import TransactionHistory from './TransactionHistory';
import TermsOfUse from './TermsOfUse';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import { getFormattedDate } from '../../common/dateHelper';
// import BillingInfo from './BillingInfo';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const tabTitleStyles = {
        fontSize: '18px',
        color: '#000',
        marginBottom: 5,
        letterSpacing: 0,
        fontWeight: 600,
        textAlign: 'right',
        // '& span:nth-of-type(1)': { textTransform: 'capitalize !important' },
        textTransform: 'none',
    };

    return makeStyles(theme => ({
        root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 0,
            // marginTop: 30,
            '& .MuiBox-root': { paddingLeft: '0px', paddingRight: '0px' },
            '& .Mui-selected': { color: '#1D8A8B' },
            '& .MuiTabs-indicator': { height: '4px' },
            '& .MuiTabs-flexContainer': { borderBottom: '1px solid #F0F2F6' },
            '& .MuiTabs-flexContainer .MuiButtonBase-root': { marginRight: '30px', padding: 0, minWidth: '3rem' },
        },

        subscriptionTab: { ...tabTitleStyles },
        billingTab: { ...tabTitleStyles },
        transactionHistoryTab: { ...tabTitleStyles },
        termsAndConditionTab: { ...tabTitleStyles },

        tabPanelsContainer: { flex: 1, overflow: 'hidden', paddingTop: '25px' },
        tabPanel: { height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        tabPanelBox: { height: '100%', overflow: 'hidden' },
        buttonBox: { display: 'flex', justifyContent: 'space-between', marginBottom: 20 },
        applyNowButton: {
            boxShadow: 'none',
            background: '#FC821F',
            borderRadius: '4px',
            color: '#fff',
            padding: '12px 40px',
            marginRight: 10,
            marginBottom: 5,
            textTransform: 'none',
            '&:hover': { backgroundColor: '#fc9b4b' },
        },
        applySaveJob: {
            boxShadow: 'none',
            borderRadius: '4px',
            backgroundColor: ' #F0F2F6',
            padding: '12px 40px',
            marginBottom: 5,
            textTransform: 'none',
            color: '#000',
            '&:hover': { backgroundColor: '#c0c1c4' },
        },
        applyButton: {
            boxShadow: 'none',
            background: '#1D8A8B',
            borderRadius: '4px',
            color: '#fff',
            padding: '12px 40px',
            marginBottom: 5,
            textTransform: 'none',
            '&:hover': { backgroundColor: '#4aa1a2' },
        },
        content: {
            overflowY: 'scroll',
            '&::-webkit-scrollbar': { width: '0.4em' },
            '&::-webkit-scrollbar-track': { '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.1)', borderRadius: '5px' },
        },

        heading: { fontWeight: 'bold', paddingBottom: 10 },
        inviteCodeContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
        inviteCode: { flex: 1 },
        applyButtonContainer: { flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: 8 },
        requestInviteCodeLinkContainer: { marginTop: 16 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        pendingAccountSetup,
        value,
        changeTabHandler,
        inviteCode,
        handleChangeInviteCode,
        handleClickApplyButton,
        inviteCodeErrorText,
        isConfirmRequestInviteCodeDialogOpen,
        handleClickConfirmRequestInviteCodeDialogYes,
        handleCloseConfirmRequestInviteCodeDialog,
        requestInviteCodeSuccessfulMessageDialogOpen,
        handleOnCloseRequestInviteCodeSuccessfulMessageDialog,
    } = params;

    // const heading = pendingAccountSetup ? 'Step 2: Subscription and Payment' : 'Subscription and Billing';
    const heading = pendingAccountSetup ? 'Step 2: Subscription' : 'Subscription';

    const defns = {
        appBar: { position: 'static', style: { background: '#fff' }, elevation: '0' },

        subscriptionTab: {
            id: `subscription-billing-tab-0`,
            'aria-controls': `subscription-billing-tab-panel-0`,
        },
        // billingTab: {
        //     id: `subscription-billing-tab-1`,
        //     'aria-controls': `subscription-billing-tab-panel-1`,
        // },
        // transactionHistoryTab: {
        //     id: `subscription-billing-tab-2`,
        //     'aria-controls': `subscription-billing-tab-panel-2`,
        // },
        // termsAndConditionTab: {
        //     id: `subscription-billing-tab-3`,
        //     'aria-controls': `subscription-billing-tab-panel-3`,
        // },
        termsAndConditionTab: {
            id: `subscription-billing-tab-1`,
            'aria-controls': `subscription-billing-tab-panel-1`,
        },
        tabs: { value, onChange: changeTabHandler, 'aria-label': 'subscription-billing tabs' },
        subscription: { inviteCode },
        heading: { variant: 'h6', value: heading },
        inviteCode: { label: 'Invite Code', value: inviteCode, onChange: handleChangeInviteCode, inputProps: { maxLength: 7 } },
        applyButton: { label: 'Apply', disabled: !inviteCode, color: 'primary', onClick: handleClickApplyButton },
        inviteCodeErrorText: { variant: 'body2', value: inviteCodeErrorText, clsRedColor: true },
        tabContainer: {},
        confirmRequestInviteCodeDialog: {
            title: CONFIRM_DIALOG_REQUEST_INVITE_CODE_HEADING,
            yesButtonLabel: 'Request',
            noButtonLabel: 'Cancel',
            onClose: handleCloseConfirmRequestInviteCodeDialog,
            onConfirm: handleClickConfirmRequestInviteCodeDialogYes,
            open: isConfirmRequestInviteCodeDialogOpen,
            message: CONFIRM_DIALOG_REQUEST_INVITE_CODE_MESSAGE,
        },
        requestInviteCodeSuccessfulMessageDialog: {
            title: MESSAGE_TITLE_REQUEST_INVITE_CODE_SUBMITTED_SUCCESSFULLY,
            children: <AfitdTypography>{MESSAGE_REQUEST_INVITE_CODE_SUBMITTED_SUCCESSFULLY}</AfitdTypography>,
            open: requestInviteCodeSuccessfulMessageDialogOpen,
            onClose: handleOnCloseRequestInviteCodeSuccessfulMessageDialog,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_API_PUBLISHABLE_KEY);

const TabPanel = props => {
    const classes = useStyles()();
    const { children, value, index, ...otherProps } = props;

    const isHidden = value !== index;

    const getDefns = () => {
        const defns = {
            tabPanel: {
                id: `subscription-billing-tab-panel-${index}`,
                role: 'tabpanel',
                'aria-labelledby': `subscription-billing-tab-${index}`,
            },
        };

        // inject classes
        setClassNameInDefns(defns, classes);

        return defns;
    };

    if (isHidden) return null;
    const defns = getDefns();

    return (
        <div {...defns.tabPanel} {...otherProps}>
            {value === index && children}
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SubscriptionAndBillingTabs = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const { userId, employerId, currentEmployer, isEmp, pendingAccountSetup } = appContext;

    const context = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = context;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [value, setValue] = useState(0);
    const [validInviteCode, setValidInviteCode] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [inviteCodeErrorText, setInviteCodeErrorText] = useState('');
    const [isConfirmRequestInviteCodeDialogOpen, setIsConfirmRequestInviteCodeDialogOpen] = useState(false);
    const [requestInviteCodeSuccessfulMessageDialogOpen, setRequestInviteCodeSuccessfulMessageDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const { companyName, city, state } = currentEmployer;
    const email = _get(currentEmployer, 'members[0].email');
    const inviteCodeRequestDt = _get(currentEmployer, 'inviteCodeRequestDt');
    const inviteCodeAlreadyRequested = !!inviteCodeRequestDt;
    const inviteCodeRequestDateText = getFormattedDate(inviteCodeRequestDt);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeInviteCode = event => {
        const value = _toUpper(event.target.value);
        setInviteCode(value);
    };

    const handleClickApplyButton = async () => {
        const result = await validateInviteCode({ code: inviteCode });
        if (!result || result?.error) {
            // display error message
            setInviteCodeErrorText(result?.error);
            setValidInviteCode(false);
        } else if (result.isValid) {
            setInviteCodeErrorText('');
            setValidInviteCode(true);
        }
    };

    const handleClickRequestInviteCodeLink = () => {
        setIsConfirmRequestInviteCodeDialogOpen(true);
    };

    const handleClickConfirmRequestInviteCodeDialogYes = async () => {
        const success = await requestEmpInviteCode({ userId, email, employerId, companyName, city, state, isEmp, setSnackbarMessage, setSnackbarErrorMessage });

        if (success) {
            setIsConfirmRequestInviteCodeDialogOpen(false);
            setRequestInviteCodeSuccessfulMessageDialogOpen(true);
        }
    };

    const handleCloseConfirmRequestInviteCodeDialog = () => {
        setIsConfirmRequestInviteCodeDialogOpen(false);
    };

    const handleOnCloseRequestInviteCodeSuccessfulMessageDialog = () => {
        setRequestInviteCodeSuccessfulMessageDialogOpen(false);
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        pendingAccountSetup,
        value,
        changeTabHandler: handleChange,
        inviteCode,
        handleChangeInviteCode,
        handleClickApplyButton,
        inviteCodeErrorText,
        isConfirmRequestInviteCodeDialogOpen,
        handleClickConfirmRequestInviteCodeDialogYes,
        handleCloseConfirmRequestInviteCodeDialog,
        requestInviteCodeSuccessfulMessageDialogOpen,
        handleOnCloseRequestInviteCodeSuccessfulMessageDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Elements stripe={stripePromise}>
            <div {...defns.root}>
                <AfitdTypography {...defns.heading} />
                {pendingAccountSetup ? (
                    <div>
                        <div {...defns.inviteCodeContainer}>
                            <AfitdTextField {...defns.inviteCode} />
                            <div {...defns.applyButtonContainer}>
                                <AfitdButton {...defns.applyButton} />
                            </div>
                        </div>
                        {validInviteCode ? null : <AfitdTypography {...defns.inviteCodeErrorText} />}
                        <div {...defns.requestInviteCodeLinkContainer}>
                            {inviteCodeAlreadyRequested ? (
                                <AfitdTypography>Invite code requested on {inviteCodeRequestDateText}.</AfitdTypography>
                            ) : (
                                <AfitdLink {...defns.requestInviteCodeLink} onClick={handleClickRequestInviteCodeLink}>
                                    Do not have an invite code?
                                </AfitdLink>
                            )}
                        </div>
                    </div>
                ) : null}
                {!pendingAccountSetup || validInviteCode ? (
                    <div {...defns.tabContainer}>
                        <AppBar {...defns.appBar}>
                            <Tabs {...defns.tabs}>
                                <Tab label="Subscription" {...defns.subscriptionTab} />
                                {/* {!pendingAccountSetup && <Tab label="My Billing Info" {...defns.billingTab} />} */}
                                {/* {!pendingAccountSetup && <Tab label="Transaction History" {...defns.transactionHistoryTab} />} */}
                                <Tab label="Terms of Use" {...defns.termsAndConditionTab} />
                            </Tabs>
                        </AppBar>
                        <div {...defns.tabPanelsContainer}>
                            <TabPanel value={value} index={0}>
                                <Subscription {...defns.subscription} />
                            </TabPanel>
                            {/* {!pendingAccountSetup ? (
                        <TabPanel value={value} index={1}>
                            <BillingInfo />
                        </TabPanel>
                    ) : (
                        <TabPanel value={value} index={1}>
                            <TermsOfUse />
                        </TabPanel>
                    )} */}
                            {/* {!pendingAccountSetup && (
                        <TabPanel value={value} index={2}>
                            <TransactionHistory />
                        </TabPanel>
                    )} */}
                            {/* <TabPanel value={value} index={3}>
                        <TermsOfUse />
                    </TabPanel> */}
                            <TabPanel value={value} index={1}>
                                <TermsOfUse />
                            </TabPanel>
                        </div>
                    </div>
                ) : null}
            </div>
            <AfitdConfirmDialog {...defns.confirmRequestInviteCodeDialog} />
            <AfitdMessageDialog {...defns.requestInviteCodeSuccessfulMessageDialog} />
        </Elements>
    );
};

export default SubscriptionAndBillingTabs;
